import React from 'react';

import { Box, Button, Flex, Spinner, Text, VStack } from '@chakra-ui/react';
import { customTheme } from 'theme';

import { ApiMethodBadge } from './ApiMethodBadge';
import { ApiRoute } from './types';
import { ApiServiceState } from './useApiReference';
import { API_SERVICES, apiOperationUrl, apiServiceUrl } from './utils';

interface Props {
  services: Record<string, ApiServiceState>;
  activeRoute?: ApiRoute;
  onOpen: (url: string) => void;
}

/**
 * The API reference's services and endpoints in the docs nav. The open
 * service lists its endpoints; the rest stay one line each.
 */
export const ApiReferenceNav = ({ services, activeRoute, onOpen }: Props) => (
  <VStack
    align="stretch"
    spacing={0.5}
    mt={0.5}
    ml={3}
    pl={2}
    borderLeft="1px solid"
    borderColor="gray.100"
  >
    {API_SERVICES.map(service => {
      const state = services[service.id];
      const isOpen = activeRoute?.service === service.id;
      const isActive = isOpen && !activeRoute?.operation;
      return (
        <Box key={service.id}>
          <Button
            variant="unstyled"
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            w="full"
            h="auto"
            py={1.5}
            px={3}
            fontSize="15px"
            fontWeight={400}
            textAlign="left"
            borderRadius={6}
            bg={isActive ? 'selectedSideNavMenuBg' : 'transparent'}
            color={isOpen ? 'primary' : customTheme.colors.gray['300']}
            _hover={{ color: 'primary' }}
            aria-current={isActive ? 'page' : undefined}
            onClick={() => onOpen(apiServiceUrl(service.id))}
          >
            {service.title}
            {state?.status === 'loading' && isOpen && <Spinner size="xs" />}
          </Button>
          {isOpen && state?.status === 'ready' && (
            <VStack align="stretch" spacing={0.5} ml={3} mt={0.5}>
              {state.operations.map(operation => {
                const selected = activeRoute?.operation === operation.id;
                return (
                  <Button
                    key={operation.id}
                    variant="unstyled"
                    display="block"
                    h="auto"
                    py={1}
                    px={2}
                    textAlign="left"
                    whiteSpace="normal"
                    fontWeight={400}
                    borderRadius={6}
                    bg={selected ? 'selectedSideNavMenuBg' : 'transparent'}
                    color={
                      selected ? 'primary' : customTheme.colors.gray['300']
                    }
                    _hover={{ color: 'primary' }}
                    aria-current={selected ? 'page' : undefined}
                    onClick={() => onOpen(apiOperationUrl(operation))}
                  >
                    <Flex gap={2} align="flex-start">
                      <Box pt="3px">
                        <ApiMethodBadge method={operation.method} size="xs" />
                      </Box>
                      <Text fontSize="14px" lineHeight={1.45}>
                        {operation.summary}
                      </Text>
                    </Flex>
                  </Button>
                );
              })}
            </VStack>
          )}
        </Box>
      );
    })}
  </VStack>
);
